/**
 * Motor de reproducción HLS, independiente del camino WebRTC.
 *
 * No depende de TCPlayer ni de la licencia de Tencent: se monta sobre un `<video>` y
 * nada más. La señal sigue entrando y saliendo por Tencent; lo único que cambia es
 * quién decodifica en el navegador del espectador.
 *
 * Elección de motor **por plataforma, no por capacidad declarada**: en iOS 26
 * `Hls.isSupported()` devuelve `true` por Managed Media Source y después la
 * reproducción se cuelga en `readyState: 1`. Medido en iPhone Safari y Chrome.
 * En iOS se usa el `<video>` nativo, que sí reproduce HLS.
 */

const IOS_UA = /iP(hone|od|ad)/

/** Cuánto se le da a la reanudación simple antes de saltar al borde del live. */
const RESUME_GRACE_MS = 1500

/**
 * Sin progreso en esta ventana se reengancha la fuente.
 *
 * Existe porque el `<video>` nativo NO avisa: Apple documenta que el reproductor pasa a
 * fallado sólo cuando no queda ninguna variante viable Y se consumió el buffer, así que ante
 * un manifest que desaparece puede quedarse girando indefinidamente sin emitir `error`. Un
 * handler de `error` no alcanza; hace falta un watchdog por tiempo. Es el mismo enfoque que
 * JW Player expone como `liveTimeout`.
 */
const NATIVE_STALL_TIMEOUT_MS = 20000

/** Reintento del reenganche, con techo. Video.js usa un debounce fijo de 30 s por el mismo
 * motivo: evitar el bucle de recargas ante un error que no es transitorio. */
const NATIVE_RETRY_BASE_MS = 2000
const NATIVE_RETRY_MAX_MS = 20000

/**
 * `MEDIA_ERR_DECODE`. Es contenido roto —discontinuidad sin marcar, cambio de codec— y
 * reasignar la misma fuente no lo arregla. Los otros dos códigos sí se reintentan:
 * `MEDIA_ERR_SRC_NOT_SUPPORTED` (4) significa que nunca llegó a ser usable, o sea manifest
 * ausente, y `MEDIA_ERR_NETWORK` (2) que estaba disponible y se cayó.
 */
const MEDIA_ERR_DECODE = 3

/** ¿Esta plataforma tiene que ir por el `<video>` nativo? */
export const prefersNativeHls = (nav) => {
  const navigatorRef = nav || (typeof navigator !== 'undefined' ? navigator : undefined)
  if (!navigatorRef) return true

  const userAgent = String(navigatorRef.userAgent || '')
  if (IOS_UA.test(userAgent)) return true

  // iPadOS 13+ se declara «Macintosh», y el touch lo distingue de una Mac... salvo que
  // la Mac tenga entrada táctil. Ahí hay que exigir WebKit además: en una Mac con
  // Chrome o Firefox el `<video>` NO reproduce HLS, y como este es el camino de último
  // recurso, mandarlo al nativo sería pantalla negra sin alternativa.
  const esWebKitPuro =
    /Safari/.test(userAgent) && !/Chrome|Chromium|Edg|Firefox|OPR/.test(userAgent)
  if (/Macintosh/.test(userAgent) && Number(navigatorRef.maxTouchPoints) > 1 && esWebKitPuro) {
    return true
  }

  return false
}

/**
 * `'native' | 'hlsjs'`. `Hls` se inyecta para poder testear sin cargar la librería.
 */
export const pickHlsEngine = ({ nav, Hls } = {}) => {
  if (prefersNativeHls(nav)) return 'native'
  return Hls?.isSupported?.() ? 'hlsjs' : 'native'
}

const esConstructorDeHls = (candidato) =>
  typeof candidato === 'function' && typeof candidato.isSupported === 'function'

/**
 * Si alguien ya publicó hls.js en `window.Hls` se reutiliza para no cargar otros
 * ~540 KB en un chunk aparte. Se valida la forma porque en un embed `window.Hls`
 * puede ser cualquier cosa, o una versión vieja.
 */
const loadHlsDefault = async () => {
  if (typeof window !== 'undefined' && esConstructorDeHls(window.Hls)) return window.Hls
  const modulo = await import('hls.js/dist/hls.min.js')
  const candidato = modulo.default || modulo
  if (!esConstructorDeHls(candidato)) throw new Error('hls.js no expone un constructor válido')
  return candidato
}

/**
 * Monta el motor sobre un `<video>` existente.
 *
 * Devuelve `{ engine, recover, destroy }`. `destroy` y `recover` son idempotentes y
 * siempre seguros de llamar, incluso si el montaje falló a mitad de camino.
 */
export const mountHls = async ({
  videoElement,
  url,
  config,
  onEvent,
  nav,
  loadHls = loadHlsDefault,
}) => {
  if (!videoElement || !url) {
    return { engine: 'none', getLatency: () => undefined, recover: () => {}, destroy: () => {} }
  }

  const emit = (name, detail) => {
    try {
      onEvent?.(name, detail)
    } catch (_error) {
      // Un consumidor que lanza no debe tumbar el montaje.
    }
  }

  /**
   * `forzarMute` sólo en el arranque, que es donde el autoplay lo exige. Después NO:
   * mutear en cada `play()` le pisaba al usuario el sonido que había habilitado cada
   * vez que volvía del segundo plano. Medido en iPhone 14.
   */
  const play = ({ forzarMute = false } = {}) => {
    if (forzarMute) videoElement.muted = true
    const promesa = videoElement.play?.()
    if (promesa?.catch) promesa.catch((error) => emit('play_rejected', { name: error?.name }))
  }

  const mountNativo = () => {
    videoElement.src = url
    play({ forzarMute: true })
    emit('mounted', { engine: 'native' })

    let destruido = false
    let watchdog
    let reintento
    let reintentoDelay = NATIVE_RETRY_BASE_MS

    /**
     * Reengancha la fuente. En vivo NO se restaura `currentTime`: hay que volver al borde,
     * no a donde estaba. Es lo que hace Video.js cuando `duration` es `Infinity`.
     */
    const reengancharFuente = (motivo) => {
      if (destruido) return
      try {
        videoElement.src = url
        videoElement.load?.()
        play()
        emit('reattached', { via: motivo })
      } catch (error) {
        emit('reattach_failed', { message: error?.message })
      }
    }

    const programarReintento = (motivo) => {
      if (destruido || reintento) return
      reintento = setTimeout(() => {
        reintento = undefined
        reengancharFuente(motivo)
        reintentoDelay = Math.min(reintentoDelay * 2, NATIVE_RETRY_MAX_MS)
        armarWatchdog()
      }, reintentoDelay)
    }

    const armarWatchdog = () => {
      if (destruido) return
      clearTimeout(watchdog)
      watchdog = setTimeout(() => {
        if (destruido) return
        // Con la pestaña oculta el navegador no decodifica: no es un stall real.
        if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
          armarWatchdog()
          return
        }
        if (videoElement.ended) return
        emit('native_stalled', { readyState: videoElement.readyState })
        programarReintento('watchdog')
      }, NATIVE_STALL_TIMEOUT_MS)
    }

    const alProgresar = () => {
      reintentoDelay = NATIVE_RETRY_BASE_MS
      armarWatchdog()
    }

    const alFallar = () => {
      const code = videoElement.error?.code
      if (code === MEDIA_ERR_DECODE) {
        emit('native_fatal', { code })
        return
      }
      emit('native_error', { code })
      programarReintento(`error_${code}`)
    }

    const eventosDeProgreso = ['progress', 'timeupdate', 'playing']
    eventosDeProgreso.forEach((n) => videoElement.addEventListener(n, alProgresar))
    videoElement.addEventListener('error', alFallar)
    armarWatchdog()
    // El watchdog y `visibilitychange` pueden pedir recuperación a la vez. Sin dedupe,
    // el segundo timer lee `antes` DESPUÉS del salto del primero, cree que no avanzó y
    // vuelve a saltar: doble reinicio del decodificador, o sea doble pantalla negra.
    let timerRecuperacion

    /**
     * Distancia al borde del live. En el camino nativo no hay API que la dé, así que se
     * deriva del final de `seekable`, que es lo que el navegador considera reproducible.
     */
    const latenciaNativa = () => {
      try {
        const s = videoElement.seekable
        if (!s?.length) return undefined
        return Math.max(0, s.end(s.length - 1) - videoElement.currentTime)
      } catch (_error) {
        return undefined
      }
    }

    return {
      engine: 'native',
      getLatency: latenciaNativa,
      /**
       * Recuperación al volver del segundo plano.
       *
       * Se intenta reanudar SIN saltar: Safari se recupera solo, y saltar al borde le
       * costaba varios segundos de pantalla negra por reiniciar el decodificador. Sólo
       * si el tiempo no avanza —el caso de iOS Chrome (WKWebView), donde el `<video>`
       * queda con un buffer que ya salió de la ventana viva— se salta al borde.
       */
      recover: () => {
        if (destruido || timerRecuperacion) return
        armarWatchdog()
        const antes = videoElement.currentTime
        play()

        timerRecuperacion = setTimeout(() => {
          timerRecuperacion = undefined
          if (destruido) return
          if (videoElement.currentTime > antes) {
            emit('recovered', { via: 'resume' })
            return
          }
          try {
            const seekable = videoElement.seekable
            if (seekable?.length) {
              const borde = seekable.end(seekable.length - 1)
              // Un margen chico: pedir exactamente el borde deja al player sin datos.
              if (Number.isFinite(borde)) videoElement.currentTime = Math.max(0, borde - 1)
              play()
              emit('recovered', { via: 'seek_to_live_edge' })
              return
            }
            videoElement.src = url
            videoElement.load?.()
            play()
            emit('recovered', { via: 'source_reassigned' })
          } catch (error) {
            emit('recover_failed', { message: error?.message })
          }
        }, RESUME_GRACE_MS)
      },
      destroy: () => {
        if (destruido) return
        destruido = true
        if (timerRecuperacion) clearTimeout(timerRecuperacion)
        timerRecuperacion = undefined
        clearTimeout(watchdog)
        if (reintento) clearTimeout(reintento)
        eventosDeProgreso.forEach((n) => videoElement.removeEventListener(n, alProgresar))
        videoElement.removeEventListener('error', alFallar)
        try {
          videoElement.removeAttribute('src')
          videoElement.load?.()
        } catch (_error) {
          // El elemento ya no está en el documento.
        }
      },
    }
  }

  let Hls
  const quiereHlsJs = !prefersNativeHls(nav)
  if (quiereHlsJs) {
    try {
      Hls = await loadHls()
    } catch (error) {
      emit('hlsjs_load_failed', { message: error?.message })
    }
  }

  if (pickHlsEngine({ nav, Hls }) !== 'hlsjs') return mountNativo()

  let destruido = false
  let instancia
  try {
    instancia = new Hls(config ? { ...config } : undefined)
    instancia.on(Hls.Events.ERROR, (_evento, datos) => {
      emit('hlsjs_error', { type: datos?.type, details: datos?.details, fatal: datos?.fatal })
      // Los recuperables los maneja hls.js. Ante un fatal se marca el motor como
      // muerto además de destruirlo: sin esto un `recover()` posterior llamaba
      // `startLoad` sobre una instancia destruida, y `destroy()` la destruía dos veces.
      if (datos?.fatal) {
        destruido = true
        try {
          instancia.destroy()
        } catch (_error) {
          // Ya estaba destruida.
        }
        emit('engine_dead', { details: datos?.details })
      }
    })
    instancia.loadSource(url)
    instancia.attachMedia(videoElement)
  } catch (error) {
    // Un throw acá dejaba una rejection sin dueño y la reproducción trabada sin cartel.
    // Se degrada al nativo, la misma salida que si hls.js no hubiera cargado.
    emit('hlsjs_mount_failed', { message: error?.message })
    try {
      instancia?.destroy()
    } catch (_error) {
      // Nada que liberar.
    }
    return mountNativo()
  }

  play({ forzarMute: true })
  emit('mounted', { engine: 'hlsjs', version: Hls.version })

  return {
    engine: 'hlsjs',
    /**
     * `hls.latency` es la distancia al borde que calcula hls.js. Con LL-HLS y
     * `PROGRAM-DATE-TIME` en el manifest es la medición más fiable que hay del lado del
     * cliente, y es el número que Tencent no publica para su empaquetado.
     */
    getLatency: () => {
      try {
        return typeof instancia?.latency === 'number' ? instancia.latency : undefined
      } catch (_error) {
        return undefined
      }
    },
    /** Al volver del segundo plano hls.js puede haber detenido la carga. */
    recover: () => {
      if (destruido) return
      try {
        instancia.startLoad(-1)
        play()
        emit('recovered', { via: 'start_load' })
      } catch (error) {
        emit('recover_failed', { message: error?.message })
      }
    },
    destroy: () => {
      if (destruido) return
      destruido = true
      try {
        instancia.destroy()
      } catch (_error) {
        // Ya estaba destruida.
      }
    },
  }
}
